// app/javascript/controllers/charity_payout_validation_controller.js
import { Controller } from "@hotwired/stimulus";
import JustValidate from "just-validate";

export default class extends Controller {
    static targets = ["submit"];

    connect() {
        // Stop browser-native validation
        this.element.setAttribute("novalidate", "novalidate");

        const msg = sessionStorage.getItem("charity_payout_notice");
        if (msg) {
            sessionStorage.removeItem("charity_payout_notice");

            if (window.Swal) {
                window.Swal.fire({
                    icon: "success",
                    title: msg,
                    timer: 1600,
                    showConfirmButton: false,
                    customClass: { popup: "swal2-ea-popup", title: "swal2-ea-title" }
                });
            }
        }

        // Build JV
        this.validator = new JustValidate(this.element, {
            validateBeforeSubmitting: false, // we'll control submission
            lockForm: false,
            focusInvalidField: true,
            errorFieldCssClass: "is-invalid",
        });

        // --- All your .addField rules remain exactly the same ---
        this.validator
            .addField("#eft_account_holder_name", [
                { rule: "required", errorMessage: "Account holder name is required" },
            ])
            .addField("#bank_name", [
                { rule: "required", errorMessage: "Bank name is required" },
            ])
            .addField("#eft_institution_number", [
                { rule: "required", errorMessage: "Institution number is required" },
                { validator: (v) => /^\d{3}$/.test((v||"").trim()), errorMessage: "Must be 3 digits" },
            ])
            .addField("#eft_transit_number", [
                { rule: "required", errorMessage: "Transit number is required" },
                { validator: (v) => /^\d{5}$/.test((v||"").trim()), errorMessage: "Must be 5 digits" },
            ])
            .addField("#eft_account_number", [
                { rule: "required", errorMessage: "Account number is required" },
                {
                    validator: (v) => /^\d+$/.test((v || "").trim()),
                    errorMessage: "Numbers only",
                },
                {
                    validator: (v) => {
                        const s = (v || "").trim();
                        return s.length >= 5 && s.length <= 20;
                    },
                    errorMessage: "5–20 digits",
                },
            ])
            .addField("#eft_account_type", [
                { rule: "required", errorMessage: "Account type is required" },
            ])
            .addField("#certified_true_and_correct", [
                { rule: "required", errorMessage: "You must certify the information is correct" },
            ]);

        // NO LONGER NEEDED: this.validator.onSuccess(() => this._submitViaFetch());

        this.submitBtn = this.hasSubmitTarget
            ? this.submitTarget
            : this.element.querySelector('button[type="submit"]');
    }

    // This action will now be triggered by a CLICK on the submit button
    async submit(event) {
        // Prevent the click from causing a native form submission
        event.preventDefault();
        sessionStorage.setItem("charity_payout_notice", "Payout details saved.");
        const ok = await this.validator.revalidate().catch(() => false);
        if (!ok) {
            const first = this.element.querySelector(".is-invalid");
            first?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        // If validation is successful, proceed to submit
        this._submitViaFetch();
    }

    async _submitViaFetch() {
        // UI lock
        if (this.submitBtn) {
            this.submitBtn.disabled = true;
            this._origText = this.submitBtn.textContent;
            this.submitBtn.textContent = "Submitting...";
        }

        const form  = this.element;
        const fd    = new FormData(form);
        const token = document.querySelector('meta[name="csrf-token"]')?.content || "";

        try {
            const res = await fetch(form.action, {
                method: form.method || "POST",
                headers: {
                    "Accept": "text/html",
                    "X-CSRF-Token": token,
                },
                body: fd,
                credentials: "same-origin",
                redirect: "follow",
            });

            if (res.redirected) {
                window.location.assign(res.url);
                return;
            }

            const ct = res.headers.get("Content-Type") || "";
            const text = await res.text();
            if (ct.includes("text/html")) {
                document.documentElement.innerHTML = text;
            } else {
                alert("Submission failed. Please try again.");
                console.warn("Unexpected response:", res.status, ct, text);
            }
        } catch (e) {
            console.error("Payout submit failed:", e);
            alert("Network error. Please try again.");
        } finally {
            if (this.submitBtn) {
                this.submitBtn.disabled = false;
                if (this._origText) this.submitBtn.textContent = this._origText;
            }
        }
    }

    // NO LONGER NEEDED: disconnect() method can be removed as we are not adding any listeners manually

    digitsOnly(event) {
        const el = event?.target;
        if (!el) return;

        // Safety: only apply to the EFT account number field
        if (el.id !== "eft_account_number") return;

        el.value = (el.value || "").replace(/\D+/g, "");
    }
}